// Checkout modal with flat/percentage promo handling + min minutes guard + pricing fallback
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { motion } from 'framer-motion';
import { useWindowSize } from 'react-use';

import { calculatePricingWithMinutes, pricingApi } from '../utils/api/pricingApi';
import { razorPayInitiatePayment, razorPayPaymentStatus } from '../utils/api/PaymentAPI';
import { ShieldCheck } from 'lucide-react';
import {
  fetchOrgDataWithId,
  updateOrgDataWithId
} from '../store/app/organizationSlice';
import { statewise_cities } from '../utils/statewise_cities';
import { sendOTP, verifyOTP } from '../utils/api/phoneVerificationAPI';
import { validatePromoCode } from '../utils/api/promoCode';

const STATES = Object.keys(statewise_cities);

function CheckOutModal({ show, onClose }) {
  const dispatch = useDispatch();
  const { width, height } = useWindowSize();
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiOpacity, setConfettiOpacity] = useState(1);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);

  // pricing/discount states
  const [finalPrice, setFinalPrice] = useState(null);
  const [promoDiscountPct, setPromoDiscountPct] = useState(0);
  const [promoCodeId, setPromoCodeId] = useState('');
  const [promoMeta, setPromoMeta] = useState(null); // {code,type,value,minPurchaseMinutes,expiresAt,...}

  const orgDetail = useSelector((state) => state.orgData?.orgData?.organization || {});
  const userDetail = useSelector((state) => state?.user?.user);
  const userPhone = userDetail?.phone || '';

  const [formData, setFormData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    country: '',
    gstOrPanNumber: '',
    state: '',
    pincode: '',
    promoCode: '',
    selectedPlan: '',
    phone: userPhone || '',
    otp: ''
  });

  const cities = statewise_cities[formData.state] || [];

  useEffect(() => {
    if (userDetail?.phone) {
      setFormData((prev) => ({ ...prev, phone: userDetail.phone }));
    }
  }, [userDetail?.phone]);

  useEffect(() => {
    if (orgDetail?.id) {
      setFormData((prev) => ({
        ...prev,
        name: orgDetail.name || '',
        addressLine1: orgDetail.addressLine1 || '',
        addressLine2: orgDetail.addressLine2 || '',
        city: orgDetail.city || '',
        country: orgDetail.country || '',
        gstOrPanNumber: orgDetail.gstOrPanNumber || '',
        state: orgDetail.state || '',
        pincode: orgDetail.pincode || '',
      }));
    }
  }, [orgDetail]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await pricingApi();
        setPlans(Array.isArray(data) ? data : []);
      } catch {
        toast.error('Failed to fetch plans');
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    if (!formData.selectedPlan) {
      setSelectedPlanDetails(null);
      return;
    }
    const plan = plans.find(p => p.maxMinutes.toString() === formData.selectedPlan);
    setSelectedPlanDetails(plan || null);
  }, [formData.selectedPlan, plans]);

  // Reset promo when plan changes (avoid stale promo or invalid min requirement)
  useEffect(() => {
    setPromoCodeId('');
    setPromoMeta(null);
    setPromoDiscountPct(0);
  }, [selectedPlanDetails?.maxMinutes]);

  // Pricing call: minutes + promoCodeId (if any).
  // On error, fallback to pricing without promo so UI stays populated.
  useEffect(() => {
    if (!selectedPlanDetails) { setFinalPrice(null); setPromoDiscountPct(0); return; }

    const minutes = selectedPlanDetails?.maxMinutes;
    const doPricing = async () => {
      let cancelled = false;
      const setSafe = (fn) => { if (!cancelled) fn(); };

      try {
        const payload = { minutes };
        if (promoCodeId) payload.promoCodeId = promoCodeId;

        const res = await calculatePricingWithMinutes(payload);
        const pricing = res?.pricing || res?.data?.pricing || res;

        setSafe(() => {
          setFinalPrice(pricing ? { pricing } : null);
          const base = Number(pricing?.baseAmount ?? 0);
          const discAmt = Number(pricing?.discountAmount ?? 0);
          const pct = base > 0 ? Math.round((discAmt / base) * 100) : 0;
          setPromoDiscountPct(pct);
        });
      } catch (e) {
        // Show server message (e.g., "requires at least X minutes") and fallback to pricing w/o promo
        toast.error(e?.message || 'Failed to calculate pricing');

        try {
          const res2 = await calculatePricingWithMinutes({ minutes });
          const pricing2 = res2?.pricing || res2?.data?.pricing || res2;
          setSafe(() => {
            setFinalPrice(pricing2 ? { pricing: pricing2 } : null);
            setPromoDiscountPct(0);
            setPromoCodeId('');
            setPromoMeta(null);
          });
        } catch {
          setSafe(() => {
            setFinalPrice(null);
            setPromoDiscountPct(0);
            setPromoCodeId('');
            setPromoMeta(null);
          });
        }
      }

      return () => { cancelled = true; };
    };

    const cancel = doPricing();
    return () => { /* best-effort */ };
  }, [selectedPlanDetails, promoCodeId]);

  const inr = (v) => `₹${Number(v ?? 0).toFixed(2)}`;
  const discountLabel = () => {
    if (promoMeta?.type === 'percentage') {
      return `Discount (${Number(promoMeta.value).toFixed(0)}%)`;
    }
    if (promoMeta?.type) {
      return `Discount (${inr(promoMeta.value)})`;
    }
    return `Discount (${promoDiscountPct}%)`;
  };
  const discountBadgeText = () => {
    if (!promoMeta) return null;
    if (promoMeta.type === 'percentage') return `${Number(promoMeta.value).toFixed(0)}% discount applied!`;
    return `${inr(promoMeta.value)} off applied!`;
  };

  useEffect(() => {
    if (showConfetti) {
      const fade = setTimeout(() => setConfettiOpacity(0), 4000);
      const end = setTimeout(() => {
        setShowConfetti(false);
        setConfettiOpacity(1);
      }, 5000);
      return () => {
        clearTimeout(fade);
        clearTimeout(end);
      };
    }
  }, [showConfetti]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validate promo; enforce minPurchaseMinutes client-side before applying.
  const handleApplyPromo = async () => {
    if (!selectedPlanDetails) return toast.error('Please select a plan first');
    const code = formData.promoCode?.trim();
    if (!code) return toast.error('Enter a promo code');

    try {
      const res = await validatePromoCode(code);
      const data = res?.data;
      if (res?.success && data?.id) {
        const required = Number(data?.minPurchaseMinutes ?? 0);
        const minutes = Number(selectedPlanDetails?.maxMinutes ?? 0);

        if (required && minutes < required) {
          // Do NOT apply — inform user of the requirement
          toast.error(`This promo requires at least ${required} minutes. Your selected plan has ${minutes} minutes.`);
          setPromoCodeId('');
          setPromoMeta(null);
          setPromoDiscountPct(0);
          return;
        }

        setPromoCodeId(data.id);
        setPromoMeta({
          code: data.code,
          type: (data.type || '').toLowerCase(), // 'percentage' | 'flat' (or others)
          value: Number(data.value ?? 0),
          minPurchaseMinutes: required || 0,
          expiresAt: data.expiresAt,
        });
        toast.success(`Promo applied: ${data.code}`);
      } else {
        setPromoCodeId('');
        setPromoMeta(null);
        setPromoDiscountPct(0);
        toast.error('Invalid promo code');
      }
    } catch (err) {
      setPromoCodeId('');
      setPromoMeta(null);
      setPromoDiscountPct(0);
      toast.error(err?.message || 'Error validating promo code');
    }
  };

  const handleCheckout = async () => {
    const { name, addressLine1, selectedPlan, phone } = formData;
    if (!name || !addressLine1 || !selectedPlan || !phone) {
      return toast.error('Please fill required fields');
    }
    if (!orgDetail?.id) {
      return toast.error('Organization ID not found');
    }

    const orgPayload = {
      name,
      addressLine1,
      addressLine2: formData.addressLine2,
      city: formData.city,
      country: formData.country,
      gstOrPanNumber: formData.gstOrPanNumber,
      state: formData.state,
      pincode: formData.pincode,
    };

    try {
      setLoading(true);
      await dispatch(updateOrgDataWithId({ id: orgDetail.id, orgData: orgPayload }));
      await dispatch(fetchOrgDataWithId(orgDetail.id));

      const paymentPayload = {
        organizationId: orgDetail.id,
        minutes: selectedPlanDetails?.maxMinutes,
        promoCode: formData.promoCode || '',
        promoCodeId: promoCodeId || undefined,
      };

      const res = await razorPayInitiatePayment(paymentPayload);
      if (res?.success && res.data?.paymentUrl && res.data?.orderId) {
        const { paymentUrl, orderId } = res.data;
        window.open(paymentUrl, '_blank');
        toast.success('Redirecting to Razorpay...');
        setShowConfetti(true);
        setLoading(true);

        const checkInterval = setInterval(async () => {
          try {
            const statusRes = await razorPayPaymentStatus(orderId);
            if (statusRes?.data?.status === 'completed') {
              clearInterval(checkInterval);
              toast.success('Payment successful!');
              setLoading(false);
              window.location.reload();
            } else if (statusRes?.data?.status === 'failed') {
              clearInterval(checkInterval);
              toast.error('Payment failed!');
              setLoading(false);
              window.location.reload();
            }
          } catch (e) {
            clearInterval(checkInterval);
            toast.error('Error checking payment status');
            setLoading(false);
          }
        }, 2000);
      } else {
        toast.error('Payment initiation failed');
      }
    } catch (e) {
      toast.error('Payment failed');
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!formData.phone) return toast.error('Enter phone number');
    try {
      await sendOTP(formData.phone);
      setOtpSent(true);
      toast.success('OTP sent successfully');
    } catch (err) {
      toast.error('Error sending OTP');
    }
  };

  const handleVerifyOtp = async () => {
    if (!formData.otp) return toast.error('Enter OTP');
    try {
      await verifyOTP(formData.phone, formData.otp);
      setOtpVerified(true);
      toast.success('Phone number verified successfully');
    } catch (err) {
      toast.error('Error verifying OTP');
    }
  };

  // legacy helper (totals now driven by server)
  const calculateTotal = () => {
    if (!selectedPlanDetails) return 0;
    const price = selectedPlanDetails.price || 0;
    return price;
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content" style={{
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div className='modal-header'>
            <h6>Checkout</h6>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>

          <div className="modal-body p-0">
            <div className="row g-0">
              {/* Left Side - Organization Details */}
              <div className="col-lg-7" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="p-4 m-20 " style={{ marginBottom: '40px' }}>
                  <div className="d-flex align-items-center mb-4">
                    <div>
                      <h6 className="mb-1" style={{ color: '#2c3e50' }}>Organization Details</h6>
                      <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Verify your billing information</p>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold">Organization Name*</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '12px 16px',
                          fontSize: '14px'
                        }}
                        placeholder="Enter organization name"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Address Line 1*</label>
                      <input
                        type="text"
                        className="form-control"
                        name="addressLine1"
                        value={formData.addressLine1}
                        onChange={handleChange}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '12px 16px',
                          fontSize: '14px'
                        }}
                        placeholder="Street address, building, floor"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control"
                        name="addressLine2"
                        value={formData.addressLine2}
                        onChange={handleChange}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '12px 16px',
                          fontSize: '14px'
                        }}
                        placeholder="Apartment, suite, unit (optional)"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">State</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="form-select"
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '10px 16px',
                          fontSize: '14px',
                          backgroundPosition: 'right 10px center'
                        }}
                      >
                        <option value="">Select State</option>
                        {STATES.map((state, idx) => (
                          <option key={idx} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">City</label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="form-select"
                        disabled={!formData.state}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '10px 16px',
                          fontSize: '14px',
                          backgroundPosition: 'right 10px center'
                        }}
                      >
                        <option value="">Select City</option>
                        {cities.map((city, idx) => (
                          <option key={idx} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Pincode</label>
                      <input
                        type="text"
                        className="form-control"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '12px 16px',
                          fontSize: '14px'
                        }}
                        placeholder="123456"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">GST / PAN Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="gstOrPanNumber"
                        value={formData.gstOrPanNumber}
                        onChange={handleChange}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '12px 16px',
                          fontSize: '14px'
                        }}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Country</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="form-select"
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          padding: '10px 16px',
                          fontSize: '14px',
                          backgroundPosition: 'right 10px center'
                        }}
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                      </select>
                    </div>

                    {!userPhone && (
                      <>
                        <div className="col-12">
                          <label className="form-label fw-semibold">Phone Number*</label>
                          <div className="d-flex gap-2">
                            <input
                              type="tel"
                              className="form-control"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e9ecef',
                                padding: '12px 16px',
                                fontSize: '14px'
                              }}
                              placeholder="+91 XXXXX XXXXX"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={handleSendOtp}
                              disabled={otpSent || !formData.phone}
                              style={{
                                borderRadius: '8px',
                                minWidth: '100px',
                                fontWeight: '500'
                              }}
                            >
                              {otpSent ? 'Sent' : 'Send OTP'}
                            </button>
                          </div>
                        </div>

                        {otpSent && (
                          <div className="col-12">
                            <label className="form-label fw-semibold">Enter OTP</label>
                            <div className="d-flex gap-2">
                              <input
                                type="text"
                                className="form-control"
                                name="otp"
                                value={formData.otp}
                                onChange={handleChange}
                                style={{
                                  borderRadius: '8px',
                                  border: '2px solid #e9ecef',
                                  padding: '12px 16px',
                                  fontSize: '14px'
                                }}
                                placeholder="Enter 6-digit OTP"
                              />
                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleVerifyOtp}
                                style={{
                                  borderRadius: '8px',
                                  minWidth: '100px',
                                  fontWeight: '500'
                                }}
                              >
                                Verify
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Payment Card */}
              <div className="col-lg-5">
                <div className="p-4  m-10" style={{ backgroundColor: 'white' }}>
                  <div className="card border-0 shadow-sm h-100 p-10" style={{ borderRadius: '12px' }}>
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-4">
                        <div>
                          <h6 className="mb-1" style={{ color: '#2c3e50' }}>Select Plan</h6>
                          <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Choose your subscription plan</p>
                        </div>
                      </div>

                      {/* Plan Selection */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Available Plans*</label>
                        <select
                          className="form-select"
                          name="selectedPlan"
                          value={formData.selectedPlan}
                          onChange={handleChange}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e9ecef',
                            padding: '10px 16px',
                            fontSize: '14px',
                            backgroundPosition: 'right 10px center'
                          }}
                        >
                          <option value="">Choose a plan</option>
                          {plans.map((plan) => (
                            <option key={plan.id} value={plan.maxMinutes}>
                              {plan.label} - {plan.maxMinutes} Credits
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Selected Plan Details */}
                      {selectedPlanDetails && (
                        <div className="mb-4">
                          <div className="card bg-light border-0" style={{ borderRadius: '8px' }}>
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold">{selectedPlanDetails.label}</span>
                                <span className="badge bg-primary">{selectedPlanDetails.maxMinutes} Credits @{selectedPlanDetails.ratePerMinute}</span>
                              </div>
                              {finalPrice != null && (
                                <div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Base Price</span>
                                    <span className="fw-semibold">₹{(finalPrice?.pricing?.baseAmount)?.toFixed(2)}</span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">GST 18%</span>
                                    <span className="fw-semibold">₹{(finalPrice?.pricing?.taxAmount)?.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Promo Code — only when a plan is selected */}
                      {selectedPlanDetails && (
                        <div className="mb-4">
                          <label className="form-label fw-semibold">Promo Code</label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control"
                              name="promoCode"
                              value={formData.promoCode}
                              onChange={handleChange}
                              placeholder="Enter promo code"
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e9ecef',
                                padding: '12px 16px',
                                fontSize: '14px'
                              }}
                              disabled={!selectedPlanDetails}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={handleApplyPromo}
                              style={{
                                borderRadius: '8px',
                                minWidth: '80px',
                                fontWeight: '500'
                              }}
                              disabled={!selectedPlanDetails}
                            >
                              Apply
                            </button>
                          </div>
                          {(promoMeta || promoDiscountPct > 0) && (
                            <div className="mt-2">
                              <span className="badge bg-success">
                                <i className="fas fa-check me-1"></i>
                                {promoMeta ? discountBadgeText() : `${promoDiscountPct}% discount applied!`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Order Summary */}
                      {selectedPlanDetails && (
                        <div className="mb-4">
                          <div className="card border-0" style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            borderRadius: '8px'
                          }}>
                            <div className="card-body p-3">
                              <div className="mb-3 fw-bold " style={{ color: '#2c3e50', fontSize: '18px' }}>Order Summary</div>

                              <div className="d-flex justify-content-between mb-2">
                                <span>Subtotal</span>
                                <span>₹{(finalPrice?.pricing?.totalAmount)?.toFixed(2)}</span>
                              </div>

                              {Number(finalPrice?.pricing?.discountAmount ?? 0) > 0 && (
                                <div className="d-flex justify-content-between mb-2 text-success">
                                  <span>{discountLabel()}</span>
                                  <span>-{inr(finalPrice?.pricing?.discountAmount)}</span>
                                </div>
                              )}

                              <hr className="my-2" />
                              <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '16px' }}>
                                <span>Total</span>
                                <span style={{ color: '#28a745' }}>₹{(finalPrice?.pricing?.totalAmount)?.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Security Notice */}
                      <div className="mb-4">
                        <div className="d-flex align-items-center text-muted" style={{ fontSize: '12px' }}>
                          <i className="fas fa-lock me-2"></i>
                          <span>Your payment information is encrypted and secure</span>
                        </div>
                      </div>

                      {/* Checkout Button */}
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        className="btn btn-success w-100 py-3 mt-10"
                        onClick={handleCheckout}
                        disabled={
                          loading ||
                          !formData.name ||
                          !formData.addressLine1 ||
                          !formData.selectedPlan ||
                          !formData.phone
                        }
                        style={{
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '16px',
                          background: 'var(--primary-400)',
                          border: 'none',
                          boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: '1.4',
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <ShieldCheck size={16} />
                              <span>Proceed to Payment</span>
                            </div>
                          </>
                        )}
                      </motion.button>

                      <div className="text-center mt-3">
                        <button
                          className="btn btn-link text-muted"
                          onClick={onClose}
                          style={{ fontSize: '14px', textDecoration: 'none' }}
                        >
                          Cancel and Go back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Loader restricted to modal body only */}
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '0 0 16px 16px'
              }}>
                <div className="text-center">
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      border: '4px solid rgba(40, 167, 69, 0.3)',
                      borderTop: '4px solid #28a745',
                      borderRadius: '50%',
                      animation: 'greenSpin 1s linear infinite',
                      margin: '0 auto'
                    }}
                  />
                  <p className="mt-3 mb-1 fw-semibold" style={{ color: '#333', fontSize: '16px' }}>
                    Processing your payment...
                  </p>
                  <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                    Please do not close this tab or modal. Closing it may interrupt your transaction.
                  </p>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confetti Effect */}
      {showConfetti && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 1050,
            opacity: confettiOpacity,
            transition: 'opacity 1s ease-out',
          }}
        >
          <Confetti width={width} height={height} numberOfPieces={250} recycle={false} run={showConfetti} />
        </div>
      )}
    </motion.div>
  );
}

export default CheckOutModal;
